/**
 * Header Component
 * Global navigation with authentication state
 */

'use client';

import Link from 'next/link';
import { useAuth } from '@/lib/auth-context';
import { auth } from '@/lib/firebase.client';
import { signOut } from 'firebase/auth';
import { useState } from 'react';

export default function Header() {
  const { user, isAdmin, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-2 group">
            <img 
              src="/logo.png" 
              alt="Can I Use My FSA" 
              className="w-10 h-10 rounded-xl group-hover:scale-105 transition-transform"
            />
            <span className="text-xl font-bold text-gray-900 hidden sm:block">
              Can I Use My <span className="text-primary-500">FSA</span>?
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link
              href="/resources"
              className="text-gray-600 hover:text-primary-500 font-medium transition-colors"
            >
              Resources
            </Link>
            <Link
              href="/catalog"
              className="text-gray-600 hover:text-primary-500 font-medium transition-colors"
            >
              Catalog
            </Link>
            <Link
              href="/submit"
              className="text-gray-600 hover:text-primary-500 font-medium transition-colors"
            >
              Submit Item
            </Link>

            {!loading && (
              <>
                {user ? (
                  <>
                    {isAdmin && (
                      <Link
                        href="/admin"
                        className="text-purple-600 hover:text-purple-700 font-medium transition-colors"
                      >
                        Admin
                      </Link>
                    )}
                    <Link
                      href="/account"
                      className="text-gray-600 hover:text-primary-500 font-medium transition-colors"
                    >
                      My Account
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="text-gray-600 hover:text-primary-500 font-medium transition-colors"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <Link
                    href="/auth/signin"
                    className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors font-medium"
                  >
                    Sign In
                  </Link>
                )}
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-600 hover:text-primary-500 hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {mobileMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-3 animate-fade-in">
            <Link
              href="/resources"
              className="block text-gray-600 hover:text-primary-500 font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Resources
            </Link>
            <Link
              href="/catalog"
              className="block text-gray-600 hover:text-primary-500 font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Catalog
            </Link>
            <Link
              href="/submit"
              className="block text-gray-600 hover:text-primary-500 font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Submit Item
            </Link>

            {!loading && (
              <>
                {user ? (
                  <>
                    {isAdmin && (
                      <Link
                        href="/admin"
                        className="block text-purple-600 hover:text-purple-700 font-medium py-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Admin
                      </Link>
                    )}
                    <Link
                      href="/account"
                      className="block text-gray-600 hover:text-primary-500 font-medium py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Account
                    </Link>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full text-left text-gray-600 hover:text-primary-500 font-medium py-2"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <Link
                    href="/auth/signin"
                    className="block bg-primary-500 text-white text-center px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                )}
              </>
            )}
          </div>
        )}
      </nav>
    </header>
  );
}

